import React from "react";
import classnames from "classnames";
import {
    PlusIcon,
    StyledIconButton,
    VerticalDotsIcon,
} from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";

import { store as useStore } from "@components/VerticalLayout/store";
import { store, setIsPlusIconClicked, setIsEdited } from "../store";

import ErrorToast from "@components/Common/ErrorTost";
import { CustomMainColorButtonWithIcon } from "@components/Common/CustomMainColorButtonWithIcon";
import { store as appStore, setActions, setIsActionsClicked } from "@store";
import { isDisable } from "../PosProjectSetup/helpers";
import { hasNoShopSelected, hasAssociatedPeripherals } from "./helpers";
import { setEditedSelectedRows } from "./store";

interface CustomSideFilterProps {
    selectedRows: Record<string, any>[];
    setDataIsUpdated: (value: boolean) => void;
    setLocalFilterActive: (value: boolean) => void;
    isAddApplication: boolean;
    setIsAddApplication: (value: boolean) => void;
    isApplicationArchived: boolean;
    setIsApplicationArchived: (value: boolean) => void;
    setIsInInitializationModalOpened: (value: boolean) => void;
    setIsHistoryModalOpened: (value: boolean) => void;
    isHistoryModalOpened: boolean;
    setIsConfirmationModalArchived: (value: boolean) => void;
    setIsConfirmationModalActived: (value: boolean) => void;
    setSelectedRows: (rows: Record<string, any>[]) => void;
    setTypeDisplay: (value: string) => void;
    setIsDeviceAssociateModalOpened: (value: boolean) => void;
    setIsMultiDissociateModalOpened: (value: boolean) => void;
}

export function CustomSideFilter({
    selectedRows,
    setDataIsUpdated,
    setLocalFilterActive,
    isAddApplication,
    setIsAddApplication,
    isApplicationArchived,
    setIsApplicationArchived,
    setIsInInitializationModalOpened,
    setIsHistoryModalOpened,
    isHistoryModalOpened,
    setIsConfirmationModalArchived,
    setIsConfirmationModalActived,
    setSelectedRows,
    setTypeDisplay,
    setIsDeviceAssociateModalOpened,
    setIsMultiDissociateModalOpened,
}: CustomSideFilterProps) {
    const { t } = useTranslation();
    const { oneShop } = useSnapshot(useStore);
    const { numberArchived, numberActive, modify } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(appStore);

    // Gate the "Add an application" entry: must have at least one shop row
    // selected. The previous `hasMultipleShops` check forbid >1 shops and
    // blocked the multi-shop add path — relaxed to "no shop selected" so
    // the user can fan out a new app to several shops at once.
    const noShopSelected = React.useMemo(
        () => hasNoShopSelected(selectedRows, t("Shop")),
        [selectedRows, t]
    );

    const isExistPeripheral = React.useMemo(
        () => hasAssociatedPeripherals(selectedRows, t("Shop")),
        [selectedRows, t]
    );

    const applicationRows = React.useMemo(
        () =>
            Object.values(selectedRows ?? {}).filter(
                (row: any) =>
                    row?.appId !== undefined &&
                    row?.[t("Application")] !== "" &&
                    row?.subRows !== undefined
            ),
        [selectedRows, t]
    );

    const peripheralRows = React.useMemo(
        () =>
            Object.values(selectedRows ?? {}).filter(
                (row: any) => row?.peripheralType !== undefined
            ),
        [selectedRows]
    );

    const applicationShopIds = React.useMemo(
        () =>
            Array.from(new Set(applicationRows.map((row: any) => row.shopId))),
        [applicationRows]
    );

    const associateDisabled =
        applicationRows.length === 0 || isApplicationArchived;
    const dissociateDisabled = peripheralRows.length === 0;

    return (
        <div className="d-flex justify-content-start align-items-center ml-2">
            {modify === true && (
                <>
                    {isAddApplication !== true ? (
                        <span
                            onClick={() => {
                                setIsAddApplication(!isAddApplication);
                                oneShop && setSelectedRows([]);
                                setLocalFilterActive(false);
                            }}
                            className={
                                isApplicationArchived ||
                                (!oneShop && noShopSelected)
                                    ? "w-100 ml-2 not-allowed-icon__clz cmn_btn_openForm"
                                    : "w-100 ml-2 cmn_btn_openForm"
                            }
                        >
                            <CustomMainColorButtonWithIcon
                                icon="PlusIcon"
                                iconPosition="left"
                                rounded
                                variant="primary"
                            >
                                {t("Add an application")}
                            </CustomMainColorButtonWithIcon>
                        </span>
                    ) : (
                        <StyledIconButton
                            icon="PlusIcon"
                            className="bg-dark"
                            rounded
                            disabled={selectedRows.length === 0 ? false : true}
                            onClick={() => {
                                if (isEdited === true)
                                    setIsInInitializationModalOpened(true);
                                else {
                                    setIsPlusIconClicked(true);
                                }
                            }}
                        >
                            <PlusIcon height={20} width={20} fill="white" />
                        </StyledIconButton>
                    )}
                </>
            )}
            <VerticalDotsIcon
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                className="cursor-pointer cmn_drp_FRmenu"
                height={25}
                width={25}
                fill="black"
            />
            <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                <div
                    className={classnames(
                        "dropdown-item pl-1 cursor-pointer cmn_btn_FRlistAtived"
                    )}
                    onClick={() => {
                        if (isEdited === true) {
                            setIsActionsClicked(!isActionsClicked);
                            setActions(() => {
                                setIsEdited(false);
                                setTypeDisplay("notArchived");
                                setIsApplicationArchived(false);
                                setDataIsUpdated(true);
                                setIsAddApplication(false);
                            });
                        } else {
                            setTypeDisplay("notArchived");
                            setIsApplicationArchived(false);
                            setDataIsUpdated(true);
                            setIsAddApplication(false);
                        }
                    }}
                >
                    {t("Activated(s) application(s)")} ({numberActive})
                </div>
                <div
                    className={classnames(
                        "dropdown-item pl-1 cursor-pointer cmn_btn_FRlistArchived"
                    )}
                    onClick={() => {
                        if (isEdited === true) {
                            setIsActionsClicked(!isActionsClicked);
                            setActions(() => {
                                setIsEdited(false);
                                setTypeDisplay("Archived");
                                setIsApplicationArchived(true);
                                setDataIsUpdated(true);
                                setIsAddApplication(false);
                            });
                        } else {
                            setTypeDisplay("Archived");
                            setIsApplicationArchived(true);
                            setDataIsUpdated(true);
                            setIsAddApplication(false);
                        }
                    }}
                >
                    {t("Archived(s) application(s)")} ({numberArchived})
                </div>
                <div
                    className={`${classnames(
                        "dropdown-item pl-1 cursor-pointer cmn_btn_FRhistory",
                        {
                            "not-allowed-icon__clz":
                                selectedRows.length === 0 ||
                                !isDisable(selectedRows),
                        }
                    )}`}
                    onClick={() => {
                        setIsHistoryModalOpened(!isHistoryModalOpened);
                    }}
                >
                    {t("Historical")}
                </div>
                {modify === true && (
                    <>
                        <div
                            className={`${classnames(
                                "dropdown-item pl-1 cursor-pointer",
                                {
                                    "not-allowed-icon__clz":
                                        selectedRows.length === 0 ||
                                        !isDisable(selectedRows),
                                },

                                isApplicationArchived
                                    ? "cmn_btn_FRunarchive"
                                    : "cmn_btn_FRarchived"
                            )}`}
                            onClick={() => {
                                if (isApplicationArchived) {
                                    setIsConfirmationModalActived(true);
                                } else {
                                    if (isExistPeripheral) {
                                        ErrorToast(
                                            t(
                                                "Please note that you cannot archive a application associated with an device."
                                            )
                                        );
                                    } else {
                                        setIsConfirmationModalArchived(true);
                                    }
                                }
                            }}
                        >
                            {isApplicationArchived
                                ? `${t("Enable")}`
                                : `${t("Archive")}`}
                        </div>
                        <div
                            className={`${classnames(
                                "dropdown-item pl-1 cursor-pointer cmn_btn_FRassociate",
                                {
                                    "not-allowed-icon__clz": associateDisabled,
                                }
                            )}`}
                            onClick={() => {
                                if (applicationRows.length === 0) return;
                                if (applicationShopIds.length > 1) {
                                    ErrorToast(
                                        t(
                                            "Attention, multiple association is only available for applications within the same store."
                                        )
                                    );
                                    return;
                                }
                                setEditedSelectedRows(applicationRows);
                                setSelectedRows(applicationRows);
                                setIsDeviceAssociateModalOpened(true);
                            }}
                        >
                            {t("Associate")}
                        </div>
                        <div
                            className={`${classnames(
                                "dropdown-item pl-1 cursor-pointer cmn_btn_dissociate",
                                {
                                    "not-allowed-icon__clz": dissociateDisabled,
                                }
                            )}`}
                            onClick={() => {
                                if (peripheralRows.length === 0) return;
                                setIsMultiDissociateModalOpened(true);
                            }}
                        >
                            {t("Dissociate")}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
